import { isAxiosError } from "@ihr-radioedit/sdk-network";
import { useCallback, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { PWSSessionBackend, PwsViewUserResponse, COAST_MESSAGES } from "@inferno/renderer-shared-core";
import Heading from "../../../../../../core/ui/Heading.component";
import { FORM_ERRORS, SimpleInput } from "../../../forms";
import { useCoastAlerts } from "../../common/coast-alert/CoastAlerts.hook";
import { CoastAlertsDisplay } from "../../common/coast-alert/CoastAlertsDisplay.components";
import { UsersButton } from "../../common/UsersButton.components";

interface FormFields {
  password: string;
  email: string;
}

interface CoastAccountUpdateEmailProps {
  userData: PwsViewUserResponse;
  session: PWSSessionBackend;
}

export const CoastAccountUpdateEmail = ({ userData, session }: CoastAccountUpdateEmailProps) => {
  const { alerts, setSuccessAlert, setErrorAlert, clearAllAlerts } = useCoastAlerts();
  const { handleSubmit, register, errors } = useForm<FormFields>();
  const [isProcessing, setIsProcessing] = useState(false);

  const onEmailSubmit: SubmitHandler<FormFields> = useCallback(
    async ({ password, email }, event) => {
      event?.preventDefault();

      setIsProcessing(true);
      clearAllAlerts();

      try {
        await session.memberService.initEmailUpdate(userData.user.email, email, password);
        setSuccessAlert(COAST_MESSAGES.EMAIL_UPDATE_CONFIRMATION_LINK);
      } catch (e) {
        setErrorAlert((isAxiosError(e) && e.response?.data.reason) || COAST_MESSAGES.UNEXPECTED_ERROR);
      } finally {
        setIsProcessing(false);
      }
    },
    [clearAllAlerts, session.memberService, setErrorAlert, setSuccessAlert, userData.user.email],
  );

  return (
    <section className="component-coast-user-update-email account-sub-section">
      <CoastAlertsDisplay alerts={alerts} />

      <Heading level={2}>Change Email</Heading>

      <p>
        To change your email address from <strong>{userData?.user.email}</strong>, enter your new email and current
        password below.
      </p>

      <form onSubmit={handleSubmit(onEmailSubmit)}>
        <SimpleInput
          placeholder="Enter your new email"
          rules={{ required: FORM_ERRORS.required }}
          error={errors.email}
          register={register}
          label="New Email"
          name="email"
          type="email"
        />

        <SimpleInput
          placeholder="Enter your password"
          rules={{ required: FORM_ERRORS.required }}
          error={errors.password}
          register={register}
          label="Password"
          name="password"
          type="password"
        />

        <UsersButton
          disabled={!!errors.email || !!errors.password || isProcessing}
          isProcessing={isProcessing}
          label="Update email"
          type="submit"
        >
          Update
        </UsersButton>
      </form>
    </section>
  );
};
